import { useRef, useCallback, type KeyboardEvent, type ClipboardEvent, type ChangeEvent } from 'react'

interface OtpInputProps {
  appearance?: 'default' | 'brand'
  length?: number
  value: string
  onChange: (code: string) => void
  disabled?: boolean
}

const OtpInput = ({ appearance = 'default', length = 6, value, onChange, disabled = false }: OtpInputProps) => {
  const inputsRef = useRef<(HTMLInputElement | null)[]>([])

  const digits = value.padEnd(length, ' ').slice(0, length).split('')

  const focusInput = useCallback((index: number) => {
    const clamped = Math.max(0, Math.min(index, length - 1))
    inputsRef.current[clamped]?.focus()
  }, [length])

  const updateDigit = useCallback((index: number, digit: string) => {
    const next = [...digits]
    next[index] = digit
    onChange(next.join(''))
  }, [digits, onChange])

  const handleChange = useCallback((index: number, e: ChangeEvent<HTMLInputElement>) => {
    const char = e.target.value.replace(/\D/g, '').slice(-1)
    if (!char) return
    updateDigit(index, char)
    if (index < length - 1) focusInput(index + 1)
  }, [updateDigit, focusInput, length])

  const handleKeyDown = useCallback((index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      e.preventDefault()
      if (digits[index]) {
        updateDigit(index, '')
      } else if (index > 0) {
        updateDigit(index - 1, '')
        focusInput(index - 1)
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault()
      focusInput(index - 1)
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      e.preventDefault()
      focusInput(index + 1)
    }
  }, [digits, updateDigit, focusInput, length])

  const handlePaste = useCallback((e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length)
    if (!pasted) return
    onChange(pasted.padEnd(length, '').slice(0, length))
    focusInput(Math.min(pasted.length, length - 1))
  }, [onChange, focusInput, length])

  return (
    <div className="flex justify-center gap-2 sm:gap-3">
      {digits.map((digit, i) => (
        <input
          key={i}
          ref={(el) => { inputsRef.current[i] = el }}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={1}
          value={digit === ' ' ? '' : digit}
          onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste}
          onFocus={(e) => e.target.select()}
          disabled={disabled}
          autoComplete="one-time-code"
          aria-label={`Digit ${i + 1}`}
          className={`h-12 min-w-0 w-10 sm:h-14 sm:w-12 rounded-xl border text-center text-xl font-semibold disabled:opacity-50 transition-colors ${appearance === 'brand' ? 'auth-input' : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder:text-slate-300 dark:placeholder:text-slate-600 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20'}`}
        />
      ))}
    </div>
  )
}

export default OtpInput
